import { useEffect, useRef } from 'react'
import type { Room } from 'livekit-client'

import { removeWaitingParticipant } from '@/src/lib/waitingRoomLeave'

/**
 * Параметры хука ухода из зала при выгрузке страницы.
 *
 * `active` — true, только пока показан экран `WaitingRoom`: гость без
 * `canPublish`/`canSubscribe` и не агент/рекордер/isolate. После впуска
 * хук должен сняться, иначе уход из звонка будет дёргать запасной remove.
 */
export type UseWaitingRoomPageLeaveParams = {
  room: Room
  livekitUrl: string
  identity: string | undefined
  roomName?: string
  active: boolean
}

/**
 * Снимает гостя из зала ожидания при намеренном уходе со страницы.
 *
 * Подписывается на `pagehide` и `beforeunload`, пока `active` true. На
 * сворачивание (`visibilitychange` → hidden без выгрузки) не реагирует:
 * человек ещё может вернуться, и строка у организатора должна висеть.
 *
 * В обработчике зовём `room.disconnect()` и запасной `removeWaitingParticipant`
 * с `keepalive`. Оба вызова fire-and-forget: на выгрузке вкладки промисы
 * обычно не доходят, поэтому не ждём их и не блокируем закрытие.
 *
 * `CLIENT_INITIATED` от своего же disconnect в `handleDisconnected` уже
 * игнорируется, так что экран ошибки не покажется.
 */
export function useWaitingRoomPageLeave({
  room,
  livekitUrl,
  identity,
  roomName,
  active,
}: UseWaitingRoomPageLeaveParams): void {
  // Ref-копии, чтобы обработчик читал свежие значения без пересоздания подписки.
  const roomRef = useRef(room)
  const livekitUrlRef = useRef(livekitUrl)
  const identityRef = useRef(identity)
  const roomNameRef = useRef(roomName)
  const activeRef = useRef(active)

  roomRef.current = room
  livekitUrlRef.current = livekitUrl
  identityRef.current = identity
  roomNameRef.current = roomName
  activeRef.current = active

  useEffect(() => {
    /**
     * Намеренный уход со страницы: рвём LiveKit и шлём запасной remove.
     *
     * Оба вызова без await — на выгрузке вкладки промис не успеет.
     */
    const handleLeave = () => {
      if (!activeRef.current) {
        return
      }

      const currentRoom = roomRef.current
      const currentLivekitUrl = livekitUrlRef.current
      const currentIdentity = identityRef.current
      const currentRoomName = roomNameRef.current

      // disconnect без ожидания: на выгрузке вкладки await не успеет.
      try {
        void currentRoom.disconnect()
      } catch {
        // уже отключены — игнорируем
      }

      void removeWaitingParticipant({
        room: currentRoom,
        livekitUrl: currentLivekitUrl,
        identity: currentIdentity,
        roomName: currentRoomName,
      })
    }

    window.addEventListener('pagehide', handleLeave)
    window.addEventListener('beforeunload', handleLeave)

    return () => {
      window.removeEventListener('pagehide', handleLeave)
      window.removeEventListener('beforeunload', handleLeave)
    }
  }, [])
}
